import React from 'react';
import { Link } from 'react-router-dom';
import cx from 'classnames';
import PropTypes from 'prop-types';

import RatioContainer from 'components/consumer/RatioContainer';
import JBImage from 'components/essentials/JBImage';
import normalizeInternalLink from 'commons/normalizeInternalLink';
import useScreen from 'hooks/useScreen';

const CategoriesNavigation2Link = ({
  url,
  imageSrc,
  label,
  imgDimension,
  categoryCount,
}) => (
  <Link
    to={normalizeInternalLink(url)}
    className={cx(
      'min-h-[165px] mr-4 first:ml-0 max-[767px]:p-2 max-[767px]:min-h-[auto] focus-visible:outline-offset-0',
      {
        'w-1/5 max-[767px]:w-[33.3%]': categoryCount === 3,
        'w-[calc(22%_-_1rem)] max-[767px]:w-1/2': categoryCount === 4,
        'w-[calc(20%_-_1rem)] max-[767px]:w-[33%]': categoryCount === 5,
        'w-[calc(16.6%_-_1rem)] max-[767px]:w-[33%]': categoryCount === 6,
      }
    )}
  >
    <div className="first:ml-0 w-full h-full">
      <RatioContainer className="flex justify-center overflow-hidden md:mb-6 md:mt-4">
        <JBImage
          src={imageSrc}
          width={imgDimension}
          height={imgDimension}
          loader={false}
          crop="center"
          fit="crop"
          trimPad={20}
          ar="1:1"
        />
      </RatioContainer>

      {!!label && (
        <div className="text-lg text-left font-bold md:mb-2 min-[1440px]:mb-6 max-[767px]:ml-[5%] max-[767px]:mt-1">
          {label}
        </div>
      )}
    </div>
  </Link>
);

CategoriesNavigation2Link.propTypes = {
  categoryCount: PropTypes.number,
  imageSrc: PropTypes.string.isRequired,
  imgDimension: PropTypes.number.isRequired,
  label: PropTypes.string,
  url: PropTypes.string.isRequired,
};

const CategoriesNavigation2Image = ({ imageSrc, label, imgDimension }) => (
  <div className="first:ml-0 w-full h-full">
    <RatioContainer className="flex justify-center overflow-hidden md:mb-6 md:mt-4">
      <JBImage
        src={imageSrc}
        width={imgDimension}
        height={imgDimension}
        loader={false}
        crop="center"
        fit="crop"
        trimPad={20}
        ar="1:1"
      />
    </RatioContainer>

    {!!label && (
      <div className="text-lg text-left font-bold md:mb-2 min-[1440px]:mb-6 max-[767px]:ml-[5%] max-[767px]:mt-1">
        {label}
      </div>
    )}
  </div>
);

CategoriesNavigation2Image.propTypes = {
  imageSrc: PropTypes.string.isRequired,
  imgDimension: PropTypes.number.isRequired,
  label: PropTypes.string,
};

const CategoriesNavigation2 = props => {
  const {
    config: { categories = [] },
    isAdmin = false,
  } = props;

  const { isMobileOrTablet, windowWidth } = useScreen();
  const imgDimension = isMobileOrTablet || windowWidth < 1440 ? 323 : 298;

  const categoryCount = categories.length;

  return (
    <div className="text-[.9rem] z-20 relative overflow-auto pb-3 pl-[3.333vw] pr-[3.333vw] min-[1440px]:text-base [&_a]:bg-white [&_a]:rounded-sm [&_a]:overflow-hidden [&_a]:text-gray [&_a]:no-underline [&_a]:transition-[all_.3s_ease-in]">
      <div
        className={cx('mx-auto my-0 max-w-[1400px]', {
          'min-h-[300px] bg-white': isAdmin,
        })}
      >
        <div className="flex [&_img]:transition-[all_.3s_ease-in-out] [&_img]:w-full [&_img]:object-cover [&_img]:min-[1440px]:max-w-[323px] [&_img]:min-[1440px]:max-h-[323px] [&_img]:max-[767px]:h-auto [&_img:hover]:scale-110 max-[767px]:flex max-[767px]:flex-row max-[767px]:flex-wrap [&_a]:max-[767px]:items-center [&_a]:max-[767px]:flex [&_a]:max-[767px]:mr-0 [&_a]:max-[767px]:mb-2 [&_a]:max-[767px]:flex-col">
          {categories
            .filter(entry => entry.imageSrc !== '')
            .map(item => {
              if (item.url) {
                return (
                  <CategoriesNavigation2Link
                    {...item}
                    imgDimension={imgDimension}
                    categoryCount={categoryCount}
                    key={item.imageSrc}
                  />
                );
              }

              return (
                <CategoriesNavigation2Image
                  {...item}
                  imgDimension={imgDimension}
                  key={item.imageSrc}
                />
              );
            })}
        </div>
      </div>
    </div>
  );
};

CategoriesNavigation2.propTypes = {
  config: PropTypes.shape({
    categories: PropTypes.arrayOf(
      PropTypes.shape({
        imageSrc: PropTypes.string.required,
        url: PropTypes.string,
        label: PropTypes.string,
      })
    ),
  }),
  isAdmin: PropTypes.bool,
};

export default CategoriesNavigation2;
